import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Chip,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import api from '../../services/api';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';

const CHART_COLORS = {
  primary: '#8884d8',
  secondary: '#82ca9d',
  accent: '#ffc658',
  danger: '#ff7c7c',
  success: '#4ade80',
  warning: '#f59e0b'
};

const gradientCardSx = {
  width: '100%',
  borderRadius: 2,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  borderLeft: '3px solid transparent',
  backgroundImage: `
    linear-gradient(#fff, #fff),
    linear-gradient(180deg, #ec4899, #a855f7)
  `,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  backgroundColor: '#fff'
};

const metricBorderCardSx = (from, to) => ({
  width: 330,
  maxWidth: 260,
  minWidth: 360,
  borderRadius: 2,
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
  border: '2px solid transparent',
  backgroundImage: `
    linear-gradient(#fff, #fff),
    linear-gradient(180deg, ${from}, ${to})
  `,
  backgroundOrigin: 'border-box',
  backgroundClip: 'padding-box, border-box',
  backgroundColor: '#fff'
});

const EmojiIcon = ({ children, size = 18 }) => (
  <span style={{ fontSize: size, lineHeight: 1 }}>{children}</span>
);

const TitlePill = ({ title, from, to }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1.1,
      py: 0.45,
      borderRadius: '999px',
      fontWeight: 950,
      fontSize: '0.78rem',
      lineHeight: 1,
      color: '#0f172a',
      border: '1px solid rgba(0,0,0,0.08)',
      backgroundImage: `
        linear-gradient(rgba(255,255,255,0.96), rgba(255,255,255,0.96)),
        linear-gradient(90deg, ${from}, ${to})
      `,
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      whiteSpace: 'nowrap'
    }}
  >
    {title}
  </Box>
);

// ── Checklist Modal ────────────────────────────────────────────────────────

function ChecklistModal({ open, onClose, employeeName, checklistData, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'opening');

  React.useEffect(() => {
    if (open) setActiveTab(initialTab || 'opening');
  }, [open, initialTab]);

  if (!checklistData) return null;

  const opening = checklistData.opening || {};
  const closing = checklistData.closing || {};
  const report  = checklistData.report  || {};

  const tabs = [
    { key: 'opening', emoji: '📋', label: 'Opening', data: opening },
    { key: 'closing', emoji: '🌙', label: 'Closing', data: closing },
    { key: 'report',  emoji: '📄', label: 'Report',  data: report  },
  ];

  const getTabBadge = (tab) => {
  const d = tab.data || {};
  if (tab.key === 'report') return null;
  if (!d.submitted) return null;  // não feito ainda = sem badge
  return d.unchecked_count > 0 ? d.unchecked_count : null;
};

  const renderItems = (items = []) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {items.map((it) => (
        <Box
          key={it.field}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 0.6, borderRadius: 1.5,
            bgcolor: it.checked ? 'rgba(46,125,50,0.05)' : 'rgba(211,47,47,0.06)',
            border: '1px solid',
            borderColor: it.checked ? 'rgba(46,125,50,0.15)' : 'rgba(211,47,47,0.2)',
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', lineHeight: 1, flexShrink: 0 }}>
            {it.checked ? '✅' : '❌'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: it.checked ? 400 : 600,
              color: it.checked ? 'text.secondary' : 'error.main',
              fontSize: '0.8rem',
            }}
          >
            {it.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderReport = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {[
        { label: 'Activities Summary', value: report.activities_summary },
        { label: 'Incidents',          value: report.incidents          },
        { label: 'Measures Taken',     value: report.measures_taken     },
        { label: 'General Notes',      value: report.general_notes      },
        { label: 'Damaged Materials',  value: report.damaged_materials  },
      ].map(({ label, value }) => (
        <Box key={label}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}
          >
            {label}
          </Typography>
          <Box sx={{ mt: 0.4, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: '#f8f8f8', border: '1px solid #ebebeb', minHeight: 34 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: value ? 'text.primary' : 'text.disabled', whiteSpace: 'pre-wrap' }}>
              {value || '—'}
            </Typography>
          </Box>
        </Box>
      ))}
      {report.end_time && (
        <Typography variant="caption" color="text.secondary">
          End time: {report.end_time}
        </Typography>
      )}
    </Box>
  );

  const currentTab   = tabs.find((t) => t.key === activeTab);
  const notSubmitted = !currentTab?.data?.submitted;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', pb: 0.5, fontSize: '1rem' }}>
        {employeeName} — Checklists
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Tab bar */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #ebebeb' }}>
          {tabs.map((t) => {
            const isActive = t.key === activeTab;
            const badge    = getTabBadge(t);
            return (
              <Box
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                sx={{
                  flex: 1, py: 1.25, textAlign: 'center', cursor: 'pointer',
                  borderBottom: isActive ? '2px solid #1976d2' : '2px solid transparent',
                  color: isActive ? '#1976d2' : 'text.secondary',
                  fontWeight: isActive ? 700 : 400,
                  fontSize: '0.78rem',
                  position: 'relative',
                  userSelect: 'none',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                {t.emoji} {t.label}
                {badge != null && (
                  <Box sx={{
                    position: 'absolute', top: 5, right: 'calc(50% - 28px)',
                    minWidth: 15, height: 15, borderRadius: '999px',
                    bgcolor: 'error.main', color: 'white',
                    fontSize: '0.58rem', fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    px: 0.35, lineHeight: 1,
                  }}>
                    {badge}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {notSubmitted ? (
            <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
              Not submitted yet.
            </Alert>
          ) : activeTab === 'report' ? (
            renderReport()
          ) : (
            renderItems(currentTab?.data?.items || [])
          )}

          {/* Notes */}
          {!notSubmitted && activeTab !== 'report' && currentTab?.data?.notes && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}
              >
                Notes
              </Typography>
              <Box sx={{ mt: 0.4, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: '#f8f8f8', border: '1px solid #ebebeb' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                  {currentTab.data.notes}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Closing: validated tips/sales */}
          {!notSubmitted && activeTab === 'closing' &&
            (closing.validated_tips != null || closing.validated_sales != null) && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {closing.validated_sales != null && (
                <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase' }}>
                    Sales
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#15803d' }}>
                    ${closing.validated_sales.toFixed(2)}
                  </Typography>
                </Box>
              )}
              {closing.validated_tips != null && (
                <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase' }}>
                    Tips
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#b45309' }}>
                    ${closing.validated_tips.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

// ── AI Daily Summary Card ──────────────────────────────────────────────────

function AIDailySummaryCard() {
  const [summaryData, setSummaryData] = useState(null);
  const [generating, setGenerating]   = useState(false);
  const [fetching, setFetching]       = useState(true);

  const today = new Date().toLocaleDateString('en-CA');
  const from = '#6366f1';
  const to   = '#a78bfa';

  // Ao montar: busca resumo já existente para hoje — sem gastar token
  useEffect(() => {
    const fetchExisting = async () => {
      try {
        const res = await api.get(`/admin/ai-daily-summary?date=${today}`);
        if (res.data?.already_generated && res.data?.summary) {
          setSummaryData(res.data);
        }
      } catch (err) {
        console.error('fetch summary:', err);
      } finally {
        setFetching(false);
      }
    };
    fetchExisting();
  }, [today]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/admin/ai-daily-summary/generate', { date: today });
      setSummaryData({ ...res.data, already_generated: true });
    } catch (err) {
      console.error('generate:', err);
    } finally {
      setGenerating(false);
    }
  };

  const hasAlerts = summaryData?.has_alerts;

  return (
    <Card sx={metricBorderCardSx(from, to)}>
      <CardContent sx={{ p: 1.75 }}>
        {/* Header row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <TitlePill title="AI Daily Report" from={from} to={to} />
              <Button
              size="small"
              variant="outlined"
              disabled={generating || fetching}
              onClick={handleGenerate}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.62rem',
                px: 1,
                py: 0.2,
                minWidth: 0,
                borderColor: '#fe6d04',
                color:  '#fe4304',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: 'rgba(255, 119, 0, 0.06)', borderColor: '#fe6d04' },
              }}
            >
              {generating
                ? <><CircularProgress size={10} sx={{ mr: 0.5, color: from }} />Reading...</>
                : summaryData ? 'Refresh' : 'Read'}
            </Button>
            </Box>
            <Divider sx={{ my: 1.1, opacity: 0.6 }} />

            {/* Content area — fixed height so card doesn't grow */}
            <Box sx={{ height: 52, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {fetching ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={12} sx={{ color: from }} />
                  <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>
                    Loading...
                  </Typography>
                </Box>
              ) : !summaryData ? (
                <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem', fontStyle: 'italic' }}>
                  Click in "read" to get a summary of today's activity, including any important alerts or notes from the team.
                </Typography>
              ) : (
                <Box
                  sx={{
                    px: 1.1, py: 0.6,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(202, 202, 202, 0.06)',
                    border: `1px solid ${hasAlerts ? 'rgba(156, 156, 156, 0.2)' : 'rgba(136, 136, 136, 0.2)'}`,
                    maxHeight: 42,
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-line',
                      lineHeight: 1.5,
                      color: '#1b1b1b',
                      fontWeight: hasAlerts ? 600 : 400,
                      fontSize: '0.72rem',
                    }}
                  >
                    {summaryData.summary}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Right side: emoji */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, flexShrink: 0, mt: 0.2 }}>
            <EmojiIcon size={28}>🤖</EmojiIcon>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────

export const AdminOverview = ({ adminData, loading }) => {
  const [showAllClockLogs, setShowAllClockLogs] = useState(false);
  const [showAllHotels, setShowAllHotels] = useState(false);
  const [showAllTopPerformers, setShowAllTopPerformers] = useState(false);
  const [checklistModal, setChecklistModal] = useState({
    open: false, employeeId: null, employeeName: '', tab: 'opening'
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!adminData) return null;

  const openChecklist = (employeeId, employeeName, tab) =>
    setChecklistModal({ open: true, employeeId, employeeName, tab });

  // ── Checklist button helpers ─────────────────────────────────────────────
  const btnSx = (hasBadge) => ({
    position: 'relative',
    width: 26, height: 26,
    borderRadius: '50%',
    fontSize: '0.78rem',
    lineHeight: 1,
    border: hasBadge ? '1.5px solid rgba(211,47,47,0.5)' : '1px solid #e0e0e0',
    bgcolor: hasBadge ? 'rgba(211,47,47,0.06)' : '#fafafa',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0,
    transition: 'background 0.15s',
    '&:hover': { bgcolor: '#f0f0f0' },
  });

  const dotSx = {
    position: 'absolute', top: -5, right: -5,
    minWidth: 14, height: 14, borderRadius: '999px',
    bgcolor: '#d32f2f', color: 'white',
    fontSize: '0.52rem', fontWeight: 800,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    px: 0.3, lineHeight: 1, pointerEvents: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  };

    const renderChecklistButtons = (log) => {
    const cd = adminData.checklistByEmployee?.[log.employee_id]
            ?? adminData.checklistByEmployee?.[log.employee_name];
    if (!cd) return null;

    const openingBadge = cd.opening?.submitted && cd.opening.unchecked_count > 0
  ? cd.opening.unchecked_count : null;
const closingBadge = cd.closing?.submitted && cd.closing.unchecked_count > 0
  ? cd.closing.unchecked_count : null;
const reportBadge = null;

    return (
      <>
        <Box sx={btnSx(openingBadge != null)} onClick={() => openChecklist(log.employee_id, log.employee_name, 'opening')}>
          📋
          {openingBadge != null && <Box sx={dotSx}>{openingBadge}</Box>}
        </Box>
        <Box sx={btnSx(closingBadge != null)} onClick={() => openChecklist(log.employee_id, log.employee_name, 'closing')}>
          🌙
          {closingBadge != null && <Box sx={dotSx}>{closingBadge}</Box>}
        </Box>
        <Box sx={btnSx(reportBadge != null)} onClick={() => openChecklist(log.employee_id, log.employee_name, 'report')}>
          📄
          {reportBadge != null && <Box sx={dotSx}>{reportBadge}</Box>}
        </Box>
      </>
    );
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  const renderAdminMetricCard = ({ title, value, subtitle, emoji, colors }) => {
    const from = colors?.from || '#ec4899';
    const to   = colors?.to   || '#a855f7';
    return (
      <Card sx={metricBorderCardSx(from, to)}>
        <CardContent sx={{ p: 1.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <TitlePill title={String(title || '')} from={from} to={to} />
              <Divider sx={{ my: 1.1, opacity: 0.6 }} />
              <Typography variant="h5" sx={{ fontWeight: 950, lineHeight: 1.05, color: '#0f172a' }}>
                {value}
              </Typography>
              {subtitle ? (
                <Typography variant="body2" sx={{ mt: 0.7, color: 'rgba(15,23,42,0.72)', fontWeight: 700, lineHeight: 1.2 }}>
                  {subtitle}
                </Typography>
              ) : null}
            </Box>
            <Box sx={{ opacity: 0.95, flexShrink: 0, mt: 0.2 }}>
              <EmojiIcon size={28}>{emoji}</EmojiIcon>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderQuickStatsCards = () => (
    <Grid container spacing={1.25} justifyContent="center" sx={{ px: 0.5 }}>
      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
        {renderAdminMetricCard({
          title: "Today's Sales",
          value: `$${adminData.totalSalesToday.toFixed(0)}`,
          subtitle: `${adminData.totalSalesCountToday} transactions`,
          emoji: '💵',
          colors: { from: '#1D4ED8', to: '#60A5FA' }
        })}
      </Grid>
      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
        {renderAdminMetricCard({
          title: 'Active Employees',
          value: adminData.activeEmployees,
          subtitle: 'Currently working',
          emoji: '👥',
          colors: { from: '#7C3AED', to: '#C4B5FD' }
        })}
      </Grid>
      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
        {renderAdminMetricCard({
          title: 'Avg Sale',
          value: `$${adminData.avgSaleToday.toFixed(0)}`,
          subtitle: 'Per transaction',
          emoji: '🎯',
          colors: { from: '#059669', to: '#6EE7B7' }
        })}
      </Grid>
      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
        {renderAdminMetricCard({
          title: 'Items Sold Today',
          value: adminData.totalItemsToday,
          subtitle: 'Across all sales',
          emoji: '🧾',
          colors: { from: '#D97706', to: '#FCD34D' }
        })}
      </Grid>
      <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', justifyContent: 'center' }}>
        <AIDailySummaryCard />
      </Grid>
    </Grid>
  );

  const renderLiveActivity = () => (
    <Card sx={{ ...gradientCardSx, width: '365px' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
          <EmojiIcon>⏰</EmojiIcon> {`CLOCKS (${adminData.clockLogs.length})`}
        </Typography>

        <Box>
          {adminData.clockLogs
            .slice(0, showAllClockLogs ? adminData.clockLogs.length : 5)
            .map((log, index) => {
              const isWorking = log.clock_out === '--:--';
              return (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1.25, px: 1.5,
                    borderBottom:
                      index < (showAllClockLogs ? adminData.clockLogs.length - 1 : 4)
                        ? '1px solid #f0f0f0'
                        : 'none',
                  }}
                >
                  {/* Name + hotel */}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                      {log.employee_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15 }}>
                      {String(log.hotel || '').toUpperCase()}
                    </Typography>
                  </Box>

                 {/* Right side */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>

                    {/* Checklist buttons + status chip na mesma linha */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                      {renderChecklistButtons(log)}
                      <Chip
                        label={log.clock_out === '--:--' ? 'WORKING' : 'FINISHED'}
                        size="small"
                        color={log.clock_out === '--:--' ? 'success' : 'default'}
                        variant={log.clock_out === '--:--' ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 900, borderRadius: '999px' }}
                      />
                    </Box>

                    {/* Clock times embaixo */}
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                      {String(`${log.clock_in} - ${log.clock_out}`).toUpperCase()}
                    </Typography>

                  </Box>
                </Box>
              );
            })}
        </Box>

        {adminData.clockLogs.length > 5 && (
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowAllClockLogs(!showAllClockLogs)}
              sx={{ color: 'primary.main', fontWeight: 900 }}
            >
              {showAllClockLogs
                ? `SEE LESS (-${adminData.clockLogs.length - 5})`
                : `SEE MORE (+${adminData.clockLogs.length - 5})`}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderSalesByHotel = () => (
    <Card sx={{ ...gradientCardSx, width: '365px', height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
          <EmojiIcon>🏨</EmojiIcon> SALES BY HOTEL
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {adminData.salesByHotel
            .sort((a, b) => b.value - a.value)
            .slice(0, showAllHotels ? adminData.salesByHotel.length : 5)
            .map((hotel, index) => (
              <Box
                key={hotel.name}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.25, px: 1.5,
                  borderBottom:
                    index < (showAllHotels
                      ? adminData.salesByHotel.length - 1
                      : Math.min(4, adminData.salesByHotel.length - 1))
                      ? '1px solid #f0f0f0'
                      : 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Chip
                    label={index + 1}
                    size="small"
                    sx={{
                      backgroundColor:
                        index === 0 ? '#4caf50' : index === 1 ? '#2196f3' : index === 2 ? '#ff9800' : '#9e9e9e',
                      color: 'white',
                      fontWeight: 900,
                      minWidth: 28,
                      borderRadius: '999px'
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
                      {String(hotel.name || '').toUpperCase()}
                    </Typography>
                    {hotel.employees?.length > 0 && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {hotel.employees.join(', ').toUpperCase()}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 900 }}>
                    {`$${hotel.value.toFixed(0)}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {String(`${hotel.count || hotel.transactions || 0} SALES`).toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            ))}
        </Box>

        {adminData.salesByHotel.length > 5 && (
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowAllHotels(!showAllHotels)}
              sx={{ color: 'primary.main', fontWeight: 900 }}
            >
              {showAllHotels
                ? `SEE LESS (-${adminData.salesByHotel.length - 5})`
                : `SEE MORE (+${adminData.salesByHotel.length - 5})`}
            </Button>
          </Box>
        )}

        {adminData.salesByHotel.length === 0 && (
          <Alert severity="info" sx={{ fontSize: '0.875rem', mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 900 }}>
              NO HOTEL SALES DATA TODAY
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderHourlySales = () => {
    const hourlyChartData = adminData.hourlySales.map((h) => ({ ...h, hourLabel: `${h.hour}:00` }));
    return (
      <Card sx={{ ...gradientCardSx, width: '365px', height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
            <EmojiIcon>📊</EmojiIcon> TODAY&apos;S HOURLY SALES
          </Typography>

          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyChartData}>
                <defs>
                  <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.9} />
                    <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis dataKey="hourLabel" stroke="#666" />
                <YAxis stroke="#666" />
                <RechartsTooltip
                  formatter={(value, name) => [
                    Number(value || 0).toFixed(2),
                    String((name === 'sales' ? 'SALES' : 'TRANSACTIONS')).toUpperCase()
                  ]}
                />
                <Legend />
                <Bar dataKey="sales" fill="url(#salesGradient)" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderTopPerformers = () => (
    <Card sx={{ ...gradientCardSx, width: '365px' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
          <EmojiIcon>🏆</EmojiIcon> TOP PERFORMERS
        </Typography>

        <Box>
          {adminData.topEmployees
            .slice(0, showAllTopPerformers ? adminData.topEmployees.length : 5)
            .map((employee, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.25, px: 1.5,
                  borderBottom:
                    index < (showAllTopPerformers
                      ? adminData.topEmployees.length - 1
                      : Math.min(4, adminData.topEmployees.length - 1))
                      ? '1px solid #f0f0f0'
                      : 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Chip
                    label={index + 1}
                    size="small"
                    sx={{
                      backgroundColor:
                        index === 0 ? '#ffd700' : index === 1 ? '#c0c0c0' : index === 2 ? '#cd7f32' : '#e0e0e0',
                      color: index < 3 ? 'white' : 'black',
                      fontWeight: 900,
                      minWidth: 28,
                      borderRadius: '999px'
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }} noWrap>
                      {String(employee.name || '').toUpperCase()}
                    </Typography>
                    {employee.hotel && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        🏨 {String(employee.hotel).toUpperCase()}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 900 }}>
                    {`$${Number(employee.sales || 0).toFixed(0)}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {String(`${employee.count || 0} SALES`).toUpperCase()}
                  </Typography>
                </Box>
              </Box>
            ))}
        </Box>

        {adminData.topEmployees.length > 5 && (
          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowAllTopPerformers(!showAllTopPerformers)}
              sx={{ color: 'primary.main', fontWeight: 900 }}
            >
              {showAllTopPerformers
                ? `SEE LESS (-${adminData.topEmployees.length - 5})`
                : `SEE MORE (+${adminData.topEmployees.length - 5})`}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <>
      <Grid container spacing={1} justifyContent="center" sx={{ p: 1 }}>
        <Grid item xs={12}>
          {renderQuickStatsCards()}
        </Grid>

      <Grid item xs={12} container spacing={1} justifyContent="center">
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
            {renderLiveActivity()}
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
            {renderSalesByHotel()}
          </Grid>
        </Grid>

        <Grid item xs={12} container spacing={1} justifyContent="center">
          <Grid item xs={12} md={8} sx={{ display: 'flex', justifyContent: 'center' }}>
            {renderHourlySales()}
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
            {renderTopPerformers()}
          </Grid>
        </Grid>
      </Grid>

      <ChecklistModal
        open={checklistModal.open}
        onClose={() => setChecklistModal((s) => ({ ...s, open: false }))}
        employeeName={checklistModal.employeeName}
        checklistData={adminData?.checklistByEmployee?.[checklistModal.employeeId] || null}
        initialTab={checklistModal.tab}
      />
    </>
  );
};